/* Kiosk / Terminal fullscreen page — iter54.
 * Route: /kiosk  (PUBLIC — no auth required)
 *
 * Meant to run on a wall-mounted tablet. No navigation chrome, just a
 * huge fingerprint button and a live feed of the last 8 punches.
 * Auto-refresh every 30 seconds. Attempts fullscreen on user gesture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { Fingerprint, ArrowClockwise, ArrowsIn, ArrowsOut } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const b64uToBytes = (str) => {
  const s = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s + pad);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return arr.buffer;
};
const bytesToB64u = (buf) => {
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const credentialForServer = (cred) => {
  const raw = cred.response;
  const payload = {
    id: cred.id, rawId: bytesToB64u(cred.rawId), type: cred.type,
    response: { clientDataJSON: bytesToB64u(raw.clientDataJSON) },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
  if (raw.authenticatorData) payload.response.authenticatorData = bytesToB64u(raw.authenticatorData);
  if (raw.signature) payload.response.signature = bytesToB64u(raw.signature);
  if (raw.userHandle) payload.response.userHandle = bytesToB64u(raw.userHandle);
  return payload;
};
const optionsToGet = (opts) => ({
  ...opts,
  challenge: b64uToBytes(opts.challenge),
  allowCredentials: (opts.allowCredentials || []).map((c) => ({
    ...c, id: b64uToBytes(c.id),
  })),
});

export default function BiometricKiosk() {
  const [busy, setBusy] = useState(false);
  const [punches, setPunches] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [supported, setSupported] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    setSupported(!!window.PublicKeyCredential);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadPunches = useCallback(async () => {
    try {
      // Public feed — no auth. We accept the risk of exposing today's names
      // only when the terminal is unlocked ; in production, put the tablet
      // in supervised locations.
      const { data } = await axios.get(`${API}/api/staff/time-punches?date=${new Date().toISOString().slice(0,10)}&limit=8`, { withCredentials: true });
      setPunches(data.items || []);
    } catch (e) {
      // Public read isn't allowed → skip silently, terminal still works.
      setPunches([]);
    }
  }, []);

  useEffect(() => {
    loadPunches();
    const t = setInterval(loadPunches, 30000);
    return () => clearInterval(t);
  }, [loadPunches]);

  const punch = async () => {
    if (!supported) { toast.error("WebAuthn non supporté par ce navigateur"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/biometric/authenticate/challenge`, {});
      const assertion = await navigator.credentials.get({ publicKey: optionsToGet(data.options) });
      if (!assertion) throw new Error('Annulé');
      const { data: res } = await axios.post(`${API}/api/biometric/authenticate/verify`, {
        challenge_id: data.challenge_id,
        credential: credentialForServer(assertion),
        punch_type: 'auto',
      });
      const p = res.punch;
      setLastResult({
        ok: true,
        name: p.employee_name,
        type: p.type,
        time: new Date(p.timestamp).toLocaleTimeString('fr-FR'),
      });
      loadPunches();
      // Clear the banner after 6s
      setTimeout(() => setLastResult(null), 6000);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Pointage échoué';
      setLastResult({ ok: false, message: msg });
      setTimeout(() => setLastResult(null), 6000);
    } finally { setBusy(false); }
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      (el.requestFullscreen?.() || Promise.resolve()).then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false));
    }
  };

  const timeStr = useMemo(() => now.toLocaleTimeString('fr-FR'), [now]);
  const dateStr = useMemo(() => now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), [now]);

  return (
    <div ref={containerRef}
      className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#0B1D3A] to-[#003C99] text-white p-8 select-none"
      data-testid="kiosk-page">
      <Toaster position="top-center" />
      {/* Fullscreen toggle */}
      <button onClick={toggleFullscreen}
        className="fixed top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
        data-testid="kiosk-fullscreen-btn"
        aria-label="Basculer plein écran">
        {isFullscreen ? <ArrowsIn size={20} /> : <ArrowsOut size={20} />}
      </button>

      {/* Clock */}
      <div className="text-center mb-8">
        <div className="text-6xl md:text-8xl font-mono font-bold tracking-tight" data-testid="kiosk-clock">{timeStr}</div>
        <div className="mt-2 text-white/70 capitalize text-lg">{dateStr}</div>
      </div>

      {/* Result banner */}
      {lastResult && (
        <div
          className={`mb-6 px-5 py-3 rounded-lg shadow-xl backdrop-blur-md ${
            lastResult.ok ? 'bg-emerald-500/90' : 'bg-red-500/90'
          }`}
          data-testid="kiosk-last-result"
        >
          {lastResult.ok ? (
            <div className="flex items-center gap-3">
              <span className="text-3xl">{lastResult.type === 'in' ? '👋' : '👋'}</span>
              <div>
                <div className="text-xl font-bold">
                  {lastResult.type === 'in' ? 'Bienvenue' : 'Bonne journée'} · {lastResult.name}
                </div>
                <div className="text-sm text-white/90">{lastResult.type === 'in' ? 'ENTRÉE' : 'SORTIE'} enregistrée à {lastResult.time}</div>
              </div>
            </div>
          ) : (
            <div className="text-lg font-semibold">✗ {lastResult.message}</div>
          )}
        </div>
      )}

      {/* Main punch button */}
      <button
        onClick={punch}
        disabled={busy || !supported}
        className="w-64 h-64 md:w-80 md:h-80 rounded-full flex flex-col items-center justify-center bg-white text-[#0052FF] shadow-2xl hover:scale-105 active:scale-95 transition disabled:opacity-60"
        data-testid="kiosk-punch-btn"
      >
        <Fingerprint size={140} weight="duotone" />
        <span className="mt-2 text-xl font-black tracking-wider">{busy ? 'ATTENDEZ…' : 'POINTER'}</span>
      </button>

      <p className="mt-6 text-sm text-white/70 max-w-md text-center">
        Touchez le bouton puis présentez votre empreinte. L&apos;entrée / sortie est détectée automatiquement.
      </p>

      {!supported && (
        <div className="mt-4 bg-red-500/90 px-4 py-2 rounded text-sm max-w-md text-center">
          Ce navigateur ne prend pas en charge WebAuthn. Utilisez Chrome/Edge/Firefox récent sur un appareil avec capteur biométrique.
        </div>
      )}

      {/* Feed */}
      <div className="mt-10 w-full max-w-3xl">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm uppercase tracking-widest text-white/60 font-bold">Derniers pointages</h2>
          <button onClick={loadPunches} className="text-xs text-white/70 hover:text-white flex items-center gap-1">
            <ArrowClockwise size={12} /> Rafraîchir
          </button>
        </div>
        {punches.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-3 italic">
            Aucun pointage récent.
          </p>
        ) : (
          <ul className="divide-y divide-white/10 bg-white/5 rounded-lg overflow-hidden backdrop-blur-md">
            {punches.slice(0, 8).map((p) => (
              <li key={p.id} className="flex items-center justify-between p-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${p.type === 'in' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span className="font-mono text-white/60 text-xs">{new Date(p.timestamp).toLocaleTimeString('fr-FR')}</span>
                  <span className="font-medium">{p.employee_name}</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                  p.type === 'in' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'
                }`}>{p.type === 'in' ? 'ENTRÉE' : 'SORTIE'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="fixed bottom-2 text-[10px] text-white/40">
        Terminal Kiosque — Gestion École
      </p>
    </div>
  );
}
